import { useQuery } from "@tanstack/react-query";
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { ArrowLeft, Folder, Search } from "lucide-react";
import React, { useState } from "react";
import {
  collectionAPI,
  formatDuration,
  formatFileSize,
  getVideoStatusColor,
  getVideoStatusText,
  videoAPI,
} from "../../services/bunnyApi";

const BunnyVideoSelector = ({
  open,
  onCancel,
  onSelect,
  title = "Select Video (Bunny)",
  verticalOnly = false,
}) => {
  const [view, setView] = useState("collections"); // "collections" | "videos"
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const { data: collectionsData, isLoading: collectionsLoading } = useQuery({
    queryKey: ["bunny-collections-selector", searchTerm],
    queryFn: () => collectionAPI.getAll(1, 100, searchTerm || undefined),
    enabled: open && view === "collections",
  });

  const { data: videosData, isLoading: videosLoading } = useQuery({
    queryKey: ["bunny-videos-selector", selectedCollection?.guid, searchTerm],
    queryFn: () =>
      videoAPI.getAll(1, 100, searchTerm || undefined, selectedCollection?.guid),
    enabled: open && view === "videos" && !!selectedCollection,
  });

  const collections = collectionsData?.items || [];
  const allVideos = videosData?.items || [];
  const videos = verticalOnly
    ? allVideos.filter(
        (v) => v.height != null && v.width != null && v.height > v.width
      )
    : allVideos;

  const handleSelectCollection = (collection) => {
    setSelectedCollection(collection);
    setSearchTerm("");
    setView("videos");
  };

  const handleBackToCollections = () => {
    setSelectedCollection(null);
    setSearchTerm("");
    setView("collections");
  };

  const handleSelectVideo = (video) => {
    onSelect?.(video);
    onCancel?.();
    handleBackToCollections();
  };

  const handleModalClose = () => {
    handleBackToCollections();
    setSearchTerm("");
    onCancel?.();
  };

  const isLoading = view === "collections" ? collectionsLoading : videosLoading;
  const emptyMessage =
    view === "collections"
      ? "No collections found"
      : verticalOnly
        ? "No vertical videos found in this collection"
        : "No videos found in this collection";

  return (
    <Modal
      opened={open}
      onClose={handleModalClose}
      title={
        view === "videos" ? (
          <Group gap="sm" wrap="nowrap">
            <ActionIcon
              variant="subtle"
              onClick={handleBackToCollections}
              aria-label="Back"
            >
              <ArrowLeft size={16} />
            </ActionIcon>
            <Text className="app-text-muted" fw={500} truncate>
              {selectedCollection?.name}
            </Text>
          </Group>
        ) : (
          <Text fw={600}>{title}</Text>
        )
      }
      size="lg"
    >
      <TextInput
        placeholder={
          view === "collections" ? "Search collections..." : "Search videos..."
        }
        leftSection={<Search size={16} className="app-text-muted" />}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        styles={{
          input: { background: "var(--app-surface)", borderColor: "var(--app-border)", color: "var(--app-text)" },
        }}
        mb="md"
      />
      <div style={{ maxHeight: 420, overflowY: "auto" }}>
        {isLoading ? (
          <div className="flex justify-center py-10">
            <span className="app-text-muted">Loading…</span>
          </div>
        ) : view === "collections" ? (
          collections.length > 0 ? (
            <Stack gap="sm">
              {collections.map((collection) => (
                <button
                  key={collection.guid}
                  type="button"
                  onClick={() => handleSelectCollection(collection)}
                  className="app-card"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: 12,
                    borderRadius: 12,
                    cursor: "pointer",
                    width: "100%",
                    textAlign: "left",
                  }}
                >
                  <Folder size={28} style={{ color: "#2563eb" }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text fw={600} className="app-text" truncate>
                      {collection.name}
                    </Text>
                    <Text size="xs" className="app-text-muted">
                      Click to view videos
                    </Text>
                  </div>
                  <Button size="xs" variant="filled">
                    Open
                  </Button>
                </button>
              ))}
            </Stack>
          ) : (
            <Text className="app-text-muted" ta="center" py="xl">
              {emptyMessage}
            </Text>
          )
        ) : videos.length > 0 ? (
          <Stack gap="sm">
            {videos.map((video) => (
              <button
                key={video.guid}
                type="button"
                onClick={() => handleSelectVideo(video)}
                className="app-card"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: 12,
                  borderRadius: 12,
                  cursor: "pointer",
                  width: "100%",
                  textAlign: "left",
                }}
              >
                <img
                  src={videoAPI.getThumbnailUrl(
                    video.guid,
                    video.thumbnailFileName,
                    video.videoLibraryId
                  )}
                  alt={video.title}
                  style={{
                    width: 80,
                    height: 48,
                    objectFit: "cover",
                    borderRadius: 8,
                    background: "rgba(255,255,255,0.06)",
                    flexShrink: 0,
                  }}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src =
                      "data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D'http%3A//www.w3.org/2000/svg'%20width%3D'160'%20height%3D'96'%20viewBox%3D'0%200%20160%2096'%3E%3Crect%20width%3D'160'%20height%3D'96'%20rx%3D'8'%20fill%3D'%23111827'/%3E%3Cpath%20d%3D'M68%2031l34%2017-34%2017V31z'%20fill%3D'%239CA3AF'/%3E%3C/svg%3E";
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text fw={600} className="app-text" truncate>
                    {video.title}
                  </Text>
                  <Text size="xs" className="app-text-muted">
                    {formatDuration(video.length || 0)} •{" "}
                    {formatFileSize(video.storageSize || 0)} • {video.width}×
                    {video.height}
                  </Text>
                </div>
                <Badge
                  variant="light"
                  color={getVideoStatusColor(video.status) === "default" ? "gray" : getVideoStatusColor(video.status)}
                >
                  {getVideoStatusText(video.status)}
                </Badge>
                <Button size="xs" variant="filled">
                  Select
                </Button>
              </button>
            ))}
          </Stack>
        ) : (
          <Text className="app-text-muted" ta="center" py="xl">
            {emptyMessage}
          </Text>
        )}
      </div>
    </Modal>
  );
};

export default BunnyVideoSelector;

